import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

export function useFadeInOnScroll<T extends HTMLElement>(threshold = 0.05) {
  const ref = useRef<T>(null);
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(reduced);

  useEffect(() => {
    if (reduced || !ref.current) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(el);
    // Safety net: if the observer hasn't fired within 1.5s of mount
    // (fast scroll past, background tab, observer edge cases), force-reveal
    // so no content stays invisible behind a scroll animation.
    const fallback = window.setTimeout(() => setVisible(true), 1500);
    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [reduced, threshold]);

  const style: React.CSSProperties = {
    // Start at a low but non-zero opacity so content is never fully hidden,
    // even if the observer fails to fire.
    opacity: visible ? 1 : 0.08,
    transform: visible ? 'translateY(0)' : 'translateY(18px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
  };

  return { ref, style, visible };
}
